import React, { useRef, useEffect } from 'react';
import { DndContext, type DragEndEvent, type SensorDescriptor, type SensorOptions } from '@dnd-kit/core';
import { startOfMonth, endOfMonth, eachDayOfInterval, format, isSameMonth, startOfWeek, endOfWeek } from 'date-fns';
import { CalendarDay } from './CalendarDay';
import { PostCard } from './PostCard';
import { PLATFORM_ICONS, WEEK_DAYS } from '../constants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { Post } from '../types';

interface CalendarGridProps {
  displayMonths: string[];
  primaryMonth: string;
  postsByDate: Record<string, Post[]>;
  sensors: SensorDescriptor<SensorOptions>[];
  handleDragEnd: (event: DragEndEvent) => void;
  handlePostClick: (post: Post) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
}

export function CalendarGrid({
  displayMonths,
  primaryMonth,
  postsByDate,
  sensors,
  handleDragEnd,
  handlePostClick,
  scrollContainerRef,
  isExpanded,
}: CalendarGridProps) {
  const monthRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (primaryMonth && monthRefs.current[primaryMonth] && scrollContainerRef.current) {
      const monthElement = monthRefs.current[primaryMonth];
      const container = scrollContainerRef.current;

      if (!monthElement) return;

      const elementTop = monthElement.offsetTop;
      const containerHeight = container.clientHeight;
      const elementHeight = monthElement.clientHeight;

      const scrollPosition = elementTop - (containerHeight / 2) + (elementHeight / 2);
      container.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }, [primaryMonth, scrollContainerRef]);

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div
        ref={scrollContainerRef}
        className="overflow-y-auto h-full"
        data-scroll-container
      >
        {displayMonths.map((monthStr) => {
          const [year, monthNum] = monthStr.split('-').map(Number);
          const monthDate = new Date(year, monthNum - 1, 1);

          const monthStart = startOfMonth(monthDate);
          const monthEnd = endOfMonth(monthDate);
          const calendarStart = startOfWeek(monthStart);
          const calendarEnd = endOfWeek(monthEnd);

          const calendarDays = eachDayOfInterval({
            start: calendarStart,
            end: calendarEnd
          });

          const monthName = format(monthDate, 'MMMM yyyy');
          const isPrimaryMonth = monthStr === primaryMonth;

          return (
            <div
              key={monthStr}
              ref={el => { monthRefs.current[monthStr] = el; }}
              data-month={monthStr}
              className="border-b last:border-b-0"
            >
              {/* Month name with first weekday, then remaining weekdays - all in 7 columns */}
              <div className="grid grid-cols-7 border-b">
                <div className={`bg-muted font-semibold border-r flex items-center justify-between ${isExpanded ? 'py-2 px-2 text-sm' : 'py-1 px-1 text-xs'}`}>
                  <span className="text-muted-foreground">{format(monthDate, 'MMMM')}</span>
                  <span className="text-foreground">{WEEK_DAYS[0]}</span>
                </div>
                {WEEK_DAYS.slice(1).map(day => (
                  <div
                    key={day}
                    className={`bg-muted text-right font-semibold text-foreground border-r last:border-r-0 ${isExpanded ? 'py-2 px-2 text-sm' : 'py-1 px-1 text-xs'}`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days grid - now properly aligned with 7-column header */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day, idx) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const dayPosts = postsByDate[dateStr] || [];
                  const isCurrentMonth = isSameMonth(day, monthDate);
                  const dayNumber = format(day, 'd');
                  const isFirstOfMonth = dayNumber === '1';
                  const monthAbbr = isFirstOfMonth ? format(day, 'MMM') : undefined;

                  return (
                    <CalendarDay
                      key={idx}
                      dateStr={dateStr}
                      isCurrentMonth={isCurrentMonth}
                      dayNumber={dayNumber}
                      monthAbbr={monthAbbr}
                      isExpanded={isExpanded}
                      isPrimaryMonth={isPrimaryMonth}
                    >
                      {dayPosts.length > 0 && (
                        <div className={isExpanded ? 'space-y-1.5' : 'space-y-1'}>
                          {dayPosts.slice(0, 3).map((post, postIdx) => {
                            const iconSrc = PLATFORM_ICONS[post.platform];

                            return (
                              <PostCard
                                key={postIdx}
                                post={post}
                                postIdx={postIdx}
                                iconSrc={iconSrc}
                                onClick={() => handlePostClick(post)}
                              />
                            );
                          })}

                          {dayPosts.length > 3 && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  className="text-xs text-primary hover:text-primary/90 hover:bg-primary/10 px-2 py-1 rounded font-medium cursor-pointer transition-colors w-full text-left"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  +{dayPosts.length - 3} more
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-80 max-h-96 overflow-y-auto"
                                align="start"
                              >
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-sm">
                                    All posts for {format(new Date(dateStr), 'MMM d, yyyy')}
                                  </h4>
                                  {dayPosts.map((post, idx) => {
                                    const iconSrc = PLATFORM_ICONS[post.platform];
                                    return (
                                      <div
                                        key={idx}
                                        onClick={() => handlePostClick(post)}
                                        className="p-2 hover:bg-muted rounded cursor-pointer transition-colors"
                                      >
                                        <div className="flex items-center gap-2 mb-1">
                                          <img
                                            src={iconSrc}
                                            alt=""
                                            className="w-4 h-4"
                                          />
                                          <span className="text-xs font-medium">{post.time}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground line-clamp-2">
                                          {post.text}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      )}
                    </CalendarDay>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </DndContext>
  );
}
